import { Component, inject, OnInit, signal } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { WalletHistoryList } from '../wallet-history-list/wallet-history-list';
import { Params } from '../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { TransactionService } from '../../../services/transactions/transaction-service';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { PaymentTransaction } from '../../../models/payment-transaction';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { displayStatus, operationType, PaymentTransactionType } from '../../../models/enums';

@Component({
  selector: 'app-payment-history-list',
  imports: [TableComponent, WalletHistoryList],
  templateUrl: './payment-history-list.html',
  styleUrl: './payment-history-list.scss'
})
export class PaymentHistoryList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'transactionType',
    'operationType',
    'displayStatus',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    transactionType: "transactionType",
    operationType: "operationType",
    displayStatus: "displayStatus",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<PaymentTransaction>[] = [
    { header: 'ID', field: 'id', sortable: true },
    { header: 'Created At', field: 'createdAt', sortable: false, isDateTime: true },
    {
      header: 'Transaction Type',
      field: 'transactionType',
      sortable: false,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'select',
      filterOptions: enumToOptions(PaymentTransactionType),
    },
    {
      header: 'Ride ID',
      field: 'rideId',
      sortable: false,
      command: (paymentTransaction) => this.redirectToRideDetailsPage(paymentTransaction),
      commandEnabled: (row) => !!row['rideId'],
      cellClass: (value: unknown) => value ? 'text-primary underline cursor-pointer' : '',
    },
    { header: 'Name', field: 'firstName', mergedName: "lastName", sortable: false },
    {
      header: 'Email',
      field: 'email',
      sortable: false,
      command: (paymentTransaction) => this.redirectToCustomersPage(paymentTransaction),
      cellClass: 'text-primary underline cursor-pointer'
    },
    { header: 'Amount', field: 'amount', sortable: false, suffixText: "AED" },
    { header: 'Phone Number', field: 'phoneNumber', sortable: false },
    { header: 'Checkout ID', field: 'checkoutId', sortable: true },
    { header: 'Payment ID', field: 'paymentId', sortable: true },
    { header: 'Checkout Status', field: 'checkoutStatus', sortable: true, isCapitalize: true },
    { header: 'Payment Status', field: 'paymentStatus', sortable: true, isCapitalize: true },
    { header: 'Card Brand', field: 'cardBrand', sortable: false, visible: false },
    { header: 'Card Last 4 Digits', field: 'cardLast4', sortable: false, visible: false },
    {
      header: 'Three DS Required',
      field: 'threeDSRequired',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
    {
      header: 'Three DS Completed',
      field: 'threeDSCompleted',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
    {
      header: 'Verification Refunded',
      field: 'verificationRefunded',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
  ];

  paymentTransactionHistoryList = signal<PaymentTransaction[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  activeTab = 0;

  searchControl = new FormControl('');

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  operationTypeFilter: CustomFilter = {
    key: "operationType",
    label: "Operation type",
    type: "select",
    options: enumToOptions(operationType)
  }

  displayStatusFilter: CustomFilter = {
    key: "displayStatus",
    label: "Display status",
    type: "select",
    options: enumToOptions(displayStatus)
  }

  private transactionService = inject(TransactionService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getPaymentTransactionHistoryList()
  }

  getPaymentTransactionHistoryList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.transactionService.getPaymentTransactionsList(params);
      })
    )
      .subscribe((res) => {
        this.paymentTransactionHistoryList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  redirectToRideDetailsPage = (paymentTransaction: PaymentTransaction) => {
    this.router.navigate([`/rides/${paymentTransaction.rideId}`]);
  }

  redirectToCustomersPage = (paymentTransaction: PaymentTransaction) => {
    this.router.navigate(['/customers'], {
      queryParams: { search: paymentTransaction.email },
      queryParamsHandling: ''
    });
  }
}